import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Bold, Italic, Heading2, List, ListOrdered, Undo2, Redo2, Minus, ImagePlus } from 'lucide-react';

const TOOLS = [
  { id: 'bold', icon: Bold, run: (c) => c.toggleBold(), active: (e) => e.isActive('bold'), label: 'Negrita' },
  { id: 'italic', icon: Italic, run: (c) => c.toggleItalic(), active: (e) => e.isActive('italic'), label: 'Cursiva' },
  { id: 'h2', icon: Heading2, run: (c) => c.toggleHeading({ level: 2 }), active: (e) => e.isActive('heading', { level: 2 }), label: 'Título' },
  { id: 'bullet', icon: List, run: (c) => c.toggleBulletList(), active: (e) => e.isActive('bulletList'), label: 'Lista' },
  { id: 'ordered', icon: ListOrdered, run: (c) => c.toggleOrderedList(), active: (e) => e.isActive('orderedList'), label: 'Lista numerada' },
  { id: 'hr', icon: Minus, run: (c) => c.setHorizontalRule(), active: () => false, label: 'Separador' },
  { id: 'undo', icon: Undo2, run: (c) => c.undo(), active: () => false, label: 'Deshacer' },
  { id: 'redo', icon: Redo2, run: (c) => c.redo(), active: () => false, label: 'Rehacer' },
];

export const RichTextEditor = ({ value, onChange, testId = 'rich-text-editor', minHeight = 320, editable = true, sticky = true, onImageUpload }) => {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit, Image],
    editable,
    content: value || '',
    onUpdate: ({ editor: e }) => onChange?.(e.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;
  if (!editable) return <EditorContent editor={editor} className="rte rte-readonly" data-testid={testId} />;
  return (
    <div className="rounded-lg border bg-background" data-testid={testId}>
      <div className={`flex flex-wrap gap-1 border-b p-1.5 bg-background rounded-t-lg ${sticky ? 'sticky -top-6 z-10 shadow-[0_-24px_0_0_hsl(var(--background))]' : ''}`}>
        {TOOLS.map(({ id, icon: Icon, run, active, label }) => (
          <button
            key={id}
            type="button"
            title={label}
            data-testid={`${testId}-tool-${id}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => run(editor.chain().focus()).run()}
            className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors hover:bg-muted ${active(editor) ? 'bg-muted text-primary' : 'text-muted-foreground'}`}
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
        {onImageUpload && (
          <>
            <button type="button" title="Imagen" data-testid={`${testId}-tool-image`} onMouseDown={(e) => e.preventDefault()} onClick={() => fileRef.current?.click()}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors hover:bg-muted text-muted-foreground"><ImagePlus className="w-4 h-4" /></button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`${testId}-image-input`} onChange={async (e) => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (!f) return;
              const url = await onImageUpload(f);
              if (url) editor.chain().focus().setImage({ src: url }).run();
            }} />
          </>
        )}
      </div>
      <EditorContent editor={editor} className="rte px-4 py-3" style={{ minHeight }} data-testid={`${testId}-content`} />
    </div>
  );
};
